/**
 * Notes Store - Zustand store for notes state management.
 *
 * Manages notes with offline-first persistence via NotesService. Notes are
 * loaded per-user, ordered pinned-first then by most recently updated, and
 * exposed to the UI through the `selectVisibleNotes` selector which applies the
 * current search text and tag filter via the pure `filterNotes`/`sortNotes`
 * helpers.
 *
 * Mirrors `src/stores/countdownStore.ts`: state + async actions + selector,
 * reload-after-each-write to keep ordering correct, and error capture into
 * `error` followed by a re-throw so components can react to failures.
 *
 * @see Requirements 6.1, 6.4, 7.1, 7.2, 8.5, 9.6, 9.7, 13.5
 */

import { create } from 'zustand';
import type { Note, NoteLink } from '@/types';
import type { CreateNoteInput, UpdateNoteInput } from '@/services/notesService';
import {
  getAllNotes,
  createNote as createNoteService,
  updateNote as updateNoteService,
  deleteNote as deleteNoteService,
  toggleDone as toggleDoneService,
  hardDeleteNote as hardDeleteNoteService,
  addLink as addLinkService,
  updateLink as updateLinkService,
  removeLink as removeLinkService,
  addTag as addTagService,
  removeTag as removeTagService,
  setPinned as setPinnedService,
  filterNotes,
  sortNotes,
} from '@/services/notesService';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NotesState {
  /**
   * All of the user's notes (including done ones, which reuse the `deleted`
   * flag), ordered active-first, then pinned-first, then updatedAt descending.
   */
  notes: Note[];
  /** Currently selected note ID for detail/editor view. */
  selectedNoteId: string | null;
  /** Current search text (debounced in the component). */
  searchText: string;
  /** Selected tag IDs to filter by (AND semantics). */
  tagFilter: string[];
  /** Whether a notes operation is in progress. */
  isLoading: boolean;
  /** Last error message from a notes operation. */
  error: string | null;
}

export interface NotesActions {
  /** Load all of a user's notes (including done ones) from local storage. */
  loadNotes: (userId: string) => Promise<void>;
  /** Create a new note. */
  createNote: (input: CreateNoteInput, userId: string) => Promise<Note>;
  /** Update an existing note. */
  updateNote: (noteId: string, updates: UpdateNoteInput, userId: string) => Promise<void>;
  /** Soft-delete a note. */
  deleteNote: (noteId: string, userId: string) => Promise<void>;
  /** Toggle a note's done state (reuses the `deleted` flag). */
  toggleDone: (noteId: string, userId: string) => Promise<void>;
  /** Permanently delete a note. */
  hardDeleteNote: (noteId: string, userId: string) => Promise<void>;
  /** Add a link to a note. */
  addLink: (noteId: string, link: Omit<NoteLink, 'id'>, userId: string) => Promise<void>;
  /** Update an existing link on a note. */
  updateLink: (
    noteId: string,
    linkId: string,
    patch: Partial<Omit<NoteLink, 'id'>>,
    userId: string
  ) => Promise<void>;
  /** Remove a link from a note. */
  removeLink: (noteId: string, linkId: string, userId: string) => Promise<void>;
  /** Add a tag to a note. */
  addTag: (noteId: string, tagId: string, userId: string) => Promise<void>;
  /** Remove a tag from a note. */
  removeTag: (noteId: string, tagId: string, userId: string) => Promise<void>;
  /** Set a note's pinned state. */
  setPinned: (noteId: string, pinned: boolean, userId: string) => Promise<void>;
  /** Select a note for the editor/detail view (or deselect with null). */
  selectNote: (noteId: string | null) => void;
  /** Set the search text used by `selectVisibleNotes`. */
  setSearchText: (text: string) => void;
  /** Set the tag-filter IDs used by `selectVisibleNotes`. */
  setTagFilter: (tagIds: string[]) => void;
  /** Filtered + ordered notes for the BrowseBar. */
  selectVisibleNotes: () => Note[];
}

export type NotesStore = NotesState & NotesActions;

// ─── Store ───────────────────────────────────────────────────────────────────

export const useNotesStore = create<NotesStore>((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  notes: [],
  selectedNoteId: null,
  searchText: '',
  tagFilter: [],
  isLoading: false,
  error: null,

  // ── Actions ────────────────────────────────────────────────────────────────

  loadNotes: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      const notes = await getAllNotes(userId);
      set({ notes: sortNotes(notes), isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load notes';
      set({ isLoading: false, error: message });
    }
  },

  createNote: async (input: CreateNoteInput, userId: string) => {
    set({ error: null });
    try {
      const note = await createNoteService(input, userId);
      // Reload to get properly ordered notes
      const notes = await getAllNotes(userId);
      set({ notes: sortNotes(notes) });
      return note;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create note';
      set({ error: message });
      throw error;
    }
  },

  updateNote: async (noteId: string, updates: UpdateNoteInput, userId: string) => {
    set({ error: null });
    try {
      await updateNoteService(noteId, updates, userId);
      const notes = await getAllNotes(userId);
      set({ notes: sortNotes(notes) });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update note';
      set({ error: message });
      throw error;
    }
  },

  deleteNote: async (noteId: string, userId: string) => {
    set({ error: null });
    try {
      await deleteNoteService(noteId, userId);
      const notes = await getAllNotes(userId);
      set((state) => ({
        notes: sortNotes(notes),
        // Deselect if the deleted note was selected
        selectedNoteId:
          state.selectedNoteId === noteId ? null : state.selectedNoteId,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete note';
      set({ error: message });
      throw error;
    }
  },

  toggleDone: async (noteId: string, userId: string) => {
    set({ error: null });
    try {
      await toggleDoneService(noteId, userId);
      // Reload so done notes are re-sorted to the bottom.
      const notes = await getAllNotes(userId);
      set({ notes: sortNotes(notes) });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update note';
      set({ error: message });
      throw error;
    }
  },

  hardDeleteNote: async (noteId: string, userId: string) => {
    set({ error: null });
    try {
      await hardDeleteNoteService(noteId, userId);
      const notes = await getAllNotes(userId);
      set((state) => ({
        notes: sortNotes(notes),
        // Deselect if the permanently deleted note was selected.
        selectedNoteId:
          state.selectedNoteId === noteId ? null : state.selectedNoteId,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete note';
      set({ error: message });
      throw error;
    }
  },

  addLink: async (noteId: string, link: Omit<NoteLink, 'id'>, userId: string) => {
    set({ error: null });
    try {
      await addLinkService(noteId, link, userId);
      const notes = await getAllNotes(userId);
      set({ notes: sortNotes(notes) });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add link';
      set({ error: message });
      throw error;
    }
  },

  updateLink: async (
    noteId: string,
    linkId: string,
    patch: Partial<Omit<NoteLink, 'id'>>,
    userId: string
  ) => {
    set({ error: null });
    try {
      await updateLinkService(noteId, linkId, patch, userId);
      const notes = await getAllNotes(userId);
      set({ notes: sortNotes(notes) });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update link';
      set({ error: message });
      throw error;
    }
  },

  removeLink: async (noteId: string, linkId: string, userId: string) => {
    set({ error: null });
    try {
      await removeLinkService(noteId, linkId, userId);
      const notes = await getAllNotes(userId);
      set({ notes: sortNotes(notes) });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove link';
      set({ error: message });
      throw error;
    }
  },

  addTag: async (noteId: string, tagId: string, userId: string) => {
    set({ error: null });
    try {
      await addTagService(noteId, tagId, userId);
      const notes = await getAllNotes(userId);
      set({ notes: sortNotes(notes) });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add tag';
      set({ error: message });
      throw error;
    }
  },

  removeTag: async (noteId: string, tagId: string, userId: string) => {
    set({ error: null });
    try {
      await removeTagService(noteId, tagId, userId);
      const notes = await getAllNotes(userId);
      set({ notes: sortNotes(notes) });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove tag';
      set({ error: message });
      throw error;
    }
  },

  setPinned: async (noteId: string, pinned: boolean, userId: string) => {
    set({ error: null });
    try {
      await setPinnedService(noteId, pinned, userId);
      // Reload to reflect pinned-first reordering
      const notes = await getAllNotes(userId);
      set({ notes: sortNotes(notes) });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update pin state';
      set({ error: message });
      throw error;
    }
  },

  selectNote: (noteId: string | null) => {
    set({ selectedNoteId: noteId });
  },

  setSearchText: (text: string) => {
    set({ searchText: text });
  },

  setTagFilter: (tagIds: string[]) => {
    set({ tagFilter: tagIds });
  },

  selectVisibleNotes: (): Note[] => {
    const { notes, searchText, tagFilter } = get();
    // Apply the current search text and tag filter, then order for display.
    const filtered = filterNotes(notes, { searchText, tagIds: tagFilter });
    return sortNotes(filtered);
  },
}));
